import { useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import Box from '@mui/material/Box';
import Avatar from '@mui/material/Avatar';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Typography from '@mui/material/Typography';
import Divider from '@mui/material/Divider';
import Chip from '@mui/material/Chip';
import { styled, alpha } from '@mui/material/styles';
import PersonIcon from '@mui/icons-material/Person';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import LogoutIcon from '@mui/icons-material/Logout';
import ReceiptIcon from '@mui/icons-material/Receipt';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import { Role } from '@lov/shared';
import { getRoleColor, avatarMenuItems } from './navigationConfig';

interface User {
  firstName: string;
  lastName: string;
  email: string;
  role: Role;
}

interface AvatarDropdownProps {
  user: User;
  onLogout: () => void;
  isLoggingOut?: boolean;
}

const StyledAvatar = styled(Avatar, {
  shouldForwardProp: (prop) => prop !== 'roleColor',
})<{ roleColor?: string }>(({ theme, roleColor }) => ({
  width: 40,
  height: 40,
  fontSize: '0.9375rem',
  fontWeight: 600,
  fontFamily: '"IBM Plex Mono", monospace',
  backgroundColor: alpha(roleColor || theme.palette.success.main, 0.1),
  color: theme.palette.text.primary,
  border: `2px solid ${roleColor || theme.palette.success.main}`,
  cursor: 'pointer',
  transition: 'all 0.2s ease',

  '&:hover': {
    transform: 'scale(1.05)',
    boxShadow: `0 0 0 4px ${alpha(roleColor || theme.palette.success.main, 0.1)}`,
  },
}));

const StyledMenu = styled(Menu)(({ theme }) => ({
  '& .MuiPaper-root': {
    minWidth: 240,
    borderRadius: 12,
    marginTop: theme.spacing(1),
    boxShadow: '0 8px 24px rgba(0, 0, 0, 0.12)',
    border: `1px solid ${theme.palette.surface.glassBorder}`,
    backgroundColor: theme.palette.background.paper,
  },
}));

const UserSection = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2),
  borderBottom: `1px solid ${theme.palette.divider}`,
}));

const UserName = styled(Typography)(({ theme }) => ({
  fontFamily: '"IBM Plex Sans", sans-serif',
  fontSize: '0.9375rem',
  fontWeight: 600,
  color: theme.palette.text.primary,
  lineHeight: 1.3,
}));

const UserEmail = styled(Typography)(({ theme }) => ({
  fontFamily: '"IBM Plex Sans", sans-serif',
  fontSize: '0.8125rem',
  color: theme.palette.text.secondary,
  marginTop: 2,
}));

const RoleChip = styled(Chip, {
  shouldForwardProp: (prop) => prop !== 'roleColor',
})<{ roleColor?: string }>(({ theme, roleColor }) => ({
  height: 20,
  fontSize: '0.625rem',
  fontWeight: 600,
  fontFamily: '"IBM Plex Mono", monospace',
  textTransform: 'uppercase',
  letterSpacing: '0.05em',
  marginTop: theme.spacing(1),
  backgroundColor: alpha(roleColor || theme.palette.success.main, 0.1),
  color: roleColor || theme.palette.success.dark,
  '& .MuiChip-label': {
    padding: '0 8px',
  },
}));

const StyledMenuItem = styled(MenuItem)<{
  component?: React.ElementType;
  to?: string;
}>(({ theme }) => ({
  padding: theme.spacing(1.5, 2),
  transition: 'all 0.2s ease',
  '& .MuiListItemIcon-root': {
    minWidth: 36,
    color: theme.palette.text.secondary,
    transition: 'all 0.2s ease',
  },
  '& .MuiListItemText-primary': {
    fontFamily: '"IBM Plex Sans", sans-serif',
    fontSize: '0.875rem',
    fontWeight: 500,
  },
  '&:hover': {
    backgroundColor: theme.palette.surface.hover,
    transform: 'translateX(4px)',
    '& .MuiListItemIcon-root': {
      color: theme.palette.venue.gaming,
    },
  },
})) as typeof MenuItem;

const LogoutMenuItem = styled(StyledMenuItem)(({ theme }) => ({
  '&:hover': {
    backgroundColor: alpha(theme.palette.error.main, 0.08),
    '& .MuiListItemIcon-root': {
      color: theme.palette.error.main,
    },
    '& .MuiListItemText-primary': {
      color: theme.palette.error.main,
    },
  },
}));

// Icon mapping for dynamic menu items
const iconMap: Record<string, React.ComponentType<{ fontSize?: 'small' | 'medium' | 'large' }>> = {
  Person: PersonIcon,
  EmojiEvents: EmojiEventsIcon,
  Logout: LogoutIcon,
  Receipt: ReceiptIcon,
  CalendarToday: CalendarTodayIcon,
};

export function AvatarDropdown({ user, onLogout, isLoggingOut }: AvatarDropdownProps) {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const roleColor = getRoleColor(user.role);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    handleClose();
    onLogout();
  };

  const handleMenuItemClick = (action?: 'logout') => {
    if (action === 'logout') {
      handleLogout();
    } else {
      handleClose();
    }
  };

  const formatRole = (role: Role): string => {
    return role.replace(/_/g, ' ');
  };

  return (
    <>
      <StyledAvatar
        roleColor={roleColor}
        onClick={handleClick}
        aria-controls={open ? 'avatar-menu' : undefined}
        aria-haspopup="true"
        aria-expanded={open ? 'true' : undefined}
      >
        {user.firstName?.[0]}
        {user.lastName?.[0]}
      </StyledAvatar>

      <StyledMenu
        id="avatar-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        {/* User Info Section */}
        <UserSection>
          <UserName>
            {user.firstName} {user.lastName}
          </UserName>
          <UserEmail>{user.email}</UserEmail>
          <RoleChip label={formatRole(user.role)} size="small" roleColor={roleColor} />
        </UserSection>

        {/* Menu Items */}
        <Box sx={{ py: 0.5 }}>
          {avatarMenuItems.filter((item) => !item.roles || item.roles.includes(user.role)).map((item) => {
            const IconComponent = iconMap[item.icon];
            const isLogoutItem = item.action === 'logout';

            return (
              <Box key={item.label}>
                {item.dividerBefore && <Divider sx={{ my: 0.5 }} />}

                {isLogoutItem ? (
                  <LogoutMenuItem
                    onClick={() => handleMenuItemClick(item.action)}
                    disabled={isLoggingOut}
                  >
                    <ListItemIcon>
                      <IconComponent fontSize="small" />
                    </ListItemIcon>
                    <ListItemText primary={isLoggingOut ? 'Logging out...' : item.label} />
                  </LogoutMenuItem>
                ) : item.path ? (
                  <StyledMenuItem
                    component={RouterLink}
                    to={item.path}
                    onClick={() => handleMenuItemClick(item.action)}
                  >
                    <ListItemIcon>
                      <IconComponent fontSize="small" />
                    </ListItemIcon>
                    <ListItemText primary={item.label} />
                  </StyledMenuItem>
                ) : null}

                {item.dividerAfter && <Divider sx={{ my: 0.5 }} />}
              </Box>
            );
          })}
        </Box>
      </StyledMenu>
    </>
  );
}
